// External imports
import _ from 'lodash';
import pako from 'pako';
import axios from 'axios';
import { XMLParser } from 'fast-xml-parser';
import countryCodes from 'country-codes-list';

async function envelopToUpdates({ envelopeBody }) {
    const messages = (_.isArray(envelopeBody) ? envelopeBody : [envelopeBody])
        .map(b => (_.isArray(b.SendStreamRequest) ? b.SendStreamRequest : [b.SendStreamRequest])
            .map(s => (_.isArray(s.Payload) ? s.Payload : [s.Payload])
                .map(p => p.Message))).flat().flat().flat().flat();

    const updateXmls = await Promise.all(messages.map(m => new Promise((resolve, reject) => {
        const result = pako.inflate(Uint8Array.from(atob(m['#text'] || m._), c => c.charCodeAt(0)));
        resolve({ data: new TextDecoder().decode(result), subject: m['@_EmailSubject'] || m.$?.EmailSubject });
    })));

    return _.uniqWith(updateXmls.map(u => ({ 
            ...new XMLParser({ ignoreAttributes: false }).parse(u.data),
            data: u.data,
            subject: u.subject
        }), (a, b) => _.isEqual(a, b)));
}

function accountFromNativeXml({ xml }) {
    const account = {};

    account.name = '' + xml.Body.Organization.OrgHeader.FullName;

    const orgAddress = xml.Body.Organization.OrgHeader.OrgAddressCollection.OrgAddress;
    const address = _.isArray(orgAddress) ? orgAddress[0] : orgAddress;
    const addressText = `${address.Address1} ${address.Address2} ${address.City} ${countryCodeToCountry(address.CountryCode.Code)} ${address.PostCode}`;
    account.address = {
        type: 'geocode',
        text: addressText,
        id: '',
        countryCode: address.CountryCode.Code,
    };

    const orgContact = xml.Body.Organization.OrgHeader.OrgContactCollection?.OrgContact;
    account.contacts = orgContact ? (_.isArray(orgContact) ? orgContact : [orgContact]).map(c => ({
        email: c.Email, // TODO: validate email
        firstName: _.capitalize(c.ContactName.split(' ').slice(0, -1).join(' ')),
        lastName: _.capitalize(c.ContactName.split(' ').slice(-1).join(' ')),
        phone: c.Phone || c.Mobile || '',
        title: c.Title,
    })) : [];
    
    if (address.Email) {
        account.contacts.push({
            email: address.Email,
            phone: address.Phone || address.Mobile || '',
        });
    }

    const orgCompanyData = xml.Body.Organization.OrgHeader.OrgCompanyDataCollection?.OrgCompanyData;
    const branchCode = orgCompanyData.ControllingBranch?.Code || '';
    const code = xml.Body.Organization.OrgHeader.Code;

    account.uniqueId = `${branchCode}::${code}`;

    const isSupplier = orgCompanyData.IsCreditor?.toString() === 'true';
    const isAccount = orgCompanyData.IsDebtor?.toString() === 'true';

    account.tags = POSSIBLE_LABELS
        .filter(label => xml.Body.Organization.OrgHeader[label]?.toString() === 'true')
        .map(label => ({
            label: label.replace('Is', '')
        }));

    return { account, isAccount, isSupplier };
}

async function fixAccountAddressId({ account, _axios = axios, googleKey = 'AIzaSyBakZJ-07CCOWPG6Dn7u9nr1WI08aTrPic' }) {
    const { data: places } = await _axios.post('https://places.googleapis.com/v1/places:searchText', { textQuery: account.address.text }, {
        headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': googleKey, 'X-Goog-FieldMask': '*' }
    });

    return {
        ...account,
        address: {
            ...account.address,
            id: places?.places ? places.places[0].id : '',
        }
    };
}

export { accountFromNativeXml, envelopToUpdates, fixAccountAddressId, POSSIBLE_LABELS };

function countryCodeToCountry(code) {
    return (countryCodes.findOne('countryCode', code) || {}).countryNameEn || code;
}

const POSSIBLE_LABELS = [
    'IsConsignee',
    'IsConsignor',
    'IsTransportClient',
    'IsWarehouseClient',
    'IsForwarder',
    'IsShippingProvider',
    'IsAirWholesaler',
    'IsSeaWholesaler',
    'IsRailProvider',
    'IsLineHaulProvider',
    'IsMiscFreightServices',
    'IsAirCTO',
    'IsAirLine',
    'IsBroker',
    'IsContainerYard',
    'IsLocalTransport',
    'IsPackDepot',
    'IsSeaCTO',
    'IsShippingLine',
    'IsUnpackDepot',
    'IsRailHead',
    'IsRoadFreightDepot',
    'IsShippingConsortium',
    'IsFumigationContractor',
    'IsNationalAccount',
    'IsSalesLead',
    'IsCompetitor',
    'IsTempAccount',
    'IsGlobalAccount',
    'IsDistributionCentre',
    'IsControllingAgent',
    'IsControllingCustomer',
];
